import { useCallback, useEffect, useRef, useState } from 'react';
import { loadSwr, SWR_IDLE, type LoadSwrResult, type SwrRead, type SwrStatus } from '../services/swrCache';

export interface UseSwrLoadOptions {
  /** Skip the cached placeholder (a manual refresh). */
  force?: boolean;
  /** Fires once cached values are on screen, so the view can drop its skeleton. */
  onCached?: () => void;
}

/**
 * Runs a view's reads through the stale-while-revalidate cache and tracks the freshness state
 * for the DataFreshness badge. Each call to `load` supersedes the previous one: a load that
 * belongs to an earlier cluster/player (or an unmounted view) applies nothing once superseded.
 */
export function useSwrLoader() {
  const [status, setStatus] = useState<SwrStatus>(SWR_IDLE);
  const runRef = useRef(0);

  useEffect(
    () => () => {
      runRef.current += 1;
    },
    []
  );

  const load = useCallback((reads: SwrRead<any>[], options: UseSwrLoadOptions = {}): Promise<LoadSwrResult> => {
    const id = ++runRef.current;
    const isCurrent = () => runRef.current === id;
    return loadSwr(reads, {
      force: options.force,
      onCached: options.onCached,
      isCurrent,
      onStatus: (s) => {
        if (isCurrent()) setStatus(s);
      },
    });
  }, []);

  return { status, load };
}
